import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "./Admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Play, RotateCw, CheckCircle2, XCircle, Circle } from "lucide-react";
import { toast } from "sonner";

type ChecklistItem = {
  key: string;
  label: string;
  status: "pending" | "ok" | "fail" | "skip";
  detail?: string;
};

type Scenario = "full_success" | "partial_success" | "failed" | "multiple_partial" | "cancellation_refund" | "webhook_retry" | "duplicate_retries" | "mixed_partial_then_cancel";

const fmtMoney = (n: any) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n ?? 0));

const AdminRefundTest = () => {
  const [bookingId, setBookingId] = useState("");
  const [amount, setAmount] = useState("");
  const [running, setRunning] = useState<Scenario | null>(null);
  const [replaying, setReplaying] = useState(false);
  const [replayEventId, setReplayEventId] = useState("");
  const [lastResult, setLastResult] = useState<any>(null);
  const [replayResult, setReplayResult] = useState<any>(null);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);

  const runScenario = async (scenario: Scenario) => {
    if (!bookingId) return toast.error("Paste a booking ID first");
    setRunning(scenario);
    setLastResult(null);
    setChecklist([]);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-refund-test", {
        body: {
          booking_id: bookingId.trim(),
          scenario,
          amount: amount ? Number(amount) : undefined,
        },
      });
      if (error || (data as any)?.error) throw new Error(error?.message || (data as any)?.error);
      const res = data as any;
      setLastResult(res);
      if (res.event_id) setReplayEventId(res.event_id);

      // Multi-step scenarios: synthesize a high-level pass/fail checklist.
      if (scenario === "multiple_partial") {
        const fullyRefunded = Number(res.booking?.amount_paid ?? 0) < Number(res.booking?.total ?? 1);
        setChecklist([
          { key: "ok", label: "Both partial refunds processed", status: res.ok ? "ok" : "fail", detail: `${res.steps?.length ?? 0} steps` },
          { key: "cum", label: "Cumulative amount_paid reduced below total",
            status: fullyRefunded ? "ok" : "fail",
            detail: `amount_paid=${fmtMoney(res.booking?.amount_paid)} total=${fmtMoney(res.booking?.total)}` },
          { key: "recon", label: "Each refund created its own reconciliation row",
            status: (res.reconciliations?.length ?? 0) >= (res.steps?.length ?? 0) ? "ok" : "fail",
            detail: `${res.reconciliations?.length ?? 0} recent rows` },
        ]);
        return;
      }
      if (scenario === "cancellation_refund") {
        setChecklist([
          { key: "ok", label: "Cancel then refund succeeded", status: res.ok ? "ok" : "fail" },
          { key: "stock", label: "Stock audit was not double-decremented",
            status: (res.stock_audit_after - res.stock_audit_before) <= 1 ? "ok" : "fail",
            detail: `delta: ${res.stock_audit_after - res.stock_audit_before}` },
          { key: "status", label: "Booking ended in refunded state",
            status: res.booking?.payment_status === "refunded" ? "ok" : "fail",
            detail: `actual: ${res.booking?.payment_status}` },
        ]);
        return;
      }
      if (scenario === "webhook_retry") {
        setChecklist([
          { key: "first", label: "First webhook processed", status: res.first_run?.result?.status === "processed" ? "ok" : "fail" },
          { key: "idem", label: "Replay skipped due to idempotency",
            status: res.idempotency_respected ? "ok" : "fail",
            detail: res.replay?.skipped_idempotent ? "skipped_idempotent=true ✓" : "second call reprocessed — bug!" },
        ]);
        return;
      }
      if (scenario === "duplicate_retries") {
        setChecklist([
          { key: "first", label: "First retry processed and inserted reconciliation",
            status: res.first_run?.result?.status === "processed" ? "ok" : "fail",
            detail: `event=${res.first_run?.event_id?.slice(0, 24)}…` },
          { key: "second", label: "Second retry skipped (idempotent)",
            status: res.replay?.skipped_idempotent ? "ok" : "fail" },
          { key: "third", label: "Third retry skipped (idempotent)",
            status: res.replay_2?.skipped_idempotent ? "ok" : "fail" },
          { key: "rows", label: "Exactly one reconciliation row exists for the event id",
            status: res.reconciliation_rows_for_event === 1 ? "ok" : "fail",
            detail: `found: ${res.reconciliation_rows_for_event}` },
        ]);
        return;
      }
      if (scenario === "mixed_partial_then_cancel") {
        const finalStatus = res.booking?.payment_status;
        setChecklist([
          { key: "ok", label: "Both refund events processed", status: res.ok ? "ok" : "fail",
            detail: `${res.steps?.length ?? 0} steps fired` },
          { key: "status", label: "Final payment_status is refunded",
            status: finalStatus === "refunded" ? "ok" : "fail",
            detail: `actual: ${finalStatus}` },
          { key: "amount", label: "Final amount_paid ≈ 0",
            status: Number(res.booking?.amount_paid ?? 0) < 0.5 ? "ok" : "fail",
            detail: `amount_paid=${fmtMoney(res.booking?.amount_paid)}` },
          { key: "stock", label: "Stock audit did not double-restore",
            status: (res.stock_audit_after - res.stock_audit_before) <= 2 ? "ok" : "fail",
            detail: `delta rows: ${res.stock_audit_after - res.stock_audit_before}` },
          { key: "recon", label: "Two distinct reconciliation rows recorded",
            status: (res.reconciliations?.length ?? 0) >= 2 ? "ok" : "fail",
            detail: `rows: ${res.reconciliations?.length ?? 0}` },
        ]);
        return;
      }

      // Pull invoice email status for the checklist
      const { data: lastEmail } = await supabase
        .from("email_send_log").select("status,template_name,created_at")
        .contains("metadata", { booking_id: bookingId.trim() })
        .order("created_at", { ascending: false }).limit(1).maybeSingle();

      const expectedStatus = scenario === "full_success" ? "refunded"
        : scenario === "partial_success" ? "partial"
        : (res.booking?.payment_status ?? "paid");

      setChecklist([
        { key: "fn", label: "Edge function returned ok=true",
          status: res.ok ? "ok" : "fail",
          detail: `result: ${res.result?.status}` },
        { key: "recon", label: "New booking_reconciliations row was inserted",
          status: res.latest_reconciliation && res.latest_reconciliation.stripe_event_id === res.event_id ? "ok" : "fail",
          detail: res.latest_reconciliation
            ? `id=${res.latest_reconciliation.id.slice(0, 8)} event=${res.event_id.slice(0, 24)}…`
            : "no row found for synthetic event id" },
        { key: "snap", label: "Snapshot before/after values are present",
          status: res.latest_reconciliation?.before_snapshot && res.latest_reconciliation?.after_snapshot ? "ok" : "fail",
          detail: res.latest_reconciliation
            ? `paid ${fmtMoney(res.latest_reconciliation.before_snapshot?.amount_paid)} → ${fmtMoney(res.latest_reconciliation.after_snapshot?.amount_paid)}`
            : "" },
        { key: "stock", label: "addon_stock_audit captured any stock change",
          status: "skip",
          detail: `audit rows for booking: ${res.latest_stock_audit?.length ?? 0} (synthetic refund does not auto-restore stock; use partial-refund to remove add-ons)` },
        { key: "booking", label: `Booking payment_status now "${expectedStatus}"`,
          status: res.booking?.payment_status === expectedStatus ? "ok" : "fail",
          detail: `actual: ${res.booking?.payment_status}, amount_paid=${fmtMoney(res.booking?.amount_paid)}` },
        { key: "invoice", label: "Invoice / email log readable",
          status: "ok",
          detail: lastEmail
            ? `last email: ${lastEmail.template_name} · ${lastEmail.status}`
            : "no invoice email logged for this booking yet" },
      ]);
      toast.success(`Scenario ${scenario} executed`);
    } catch (e: any) {
      toast.error(e?.message ?? "Test failed");
      setChecklist((c) => c.map((it) => it.status === "pending" ? { ...it, status: "fail", detail: e?.message } : it));
    } finally {
      setRunning(null);
    }
  };

  const runReplay = async () => {
    if (!replayEventId) return toast.error("Paste a stripe event id");
    setReplaying(true);
    setReplayResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-event-replay", {
        body: { stripe_event_id: replayEventId.trim() },
      });
      if (error || (data as any)?.error) throw new Error(error?.message || (data as any)?.error);
      setReplayResult(data);
      toast.success((data as any)?.skipped_idempotent
        ? "Replay skipped — already reconciled"
        : "Replay processed");
    } catch (e: any) {
      toast.error(e?.message ?? "Replay failed");
    } finally {
      setReplaying(false);
    }
  };

  const StatusIcon = ({ s }: { s: ChecklistItem["status"] }) => {
    if (s === "ok") return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    if (s === "fail") return <XCircle className="w-4 h-4 text-destructive" />;
    if (s === "skip") return <Circle className="w-4 h-4 text-muted-foreground" />;
    return <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />;
  };

  return (
    <AdminLayout title="REFUND TEST">
      <p className="font-body text-muted-foreground mb-6 max-w-2xl">
        Simulate Stripe refund webhooks against a real booking and verify reconciliation,
        idempotency, and invoice email status end-to-end without the Stripe CLI.
      </p>

      <div className="bg-panel border border-border p-6 mb-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="bk">Booking ID</Label>
            <Input id="bk" value={bookingId} onChange={(e) => setBookingId(e.target.value)}
              placeholder="uuid of an existing booking with stripe_payment_intent" />
            <Link to="/admin/bookings" className="text-xs text-primary mt-1 inline-block">Browse bookings →</Link>
          </div>
          <div>
            <Label htmlFor="am">Optional refund amount (USD)</Label>
            <Input id="am" type="number" step="0.01" min="0" value={amount}
              onChange={(e) => setAmount(e.target.value)} placeholder="defaults: full=total, partial=total/2" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button onClick={() => runScenario("full_success")} disabled={!!running}>
            {running === "full_success" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            Full refund success
          </Button>
          <Button onClick={() => runScenario("partial_success")} disabled={!!running} variant="outline">
            {running === "partial_success" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            Partial refund success
          </Button>
          <Button onClick={() => runScenario("failed")} disabled={!!running} variant="outline" className="text-destructive">
            {running === "failed" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            refund.failed
          </Button>
          <Button onClick={() => runScenario("multiple_partial")} disabled={!!running} variant="outline">
            {running === "multiple_partial" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            2× partial (cumulative)
          </Button>
          <Button onClick={() => runScenario("cancellation_refund")} disabled={!!running} variant="outline">
            {running === "cancellation_refund" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            Cancel + refund
          </Button>
          <Button onClick={() => runScenario("webhook_retry")} disabled={!!running} variant="outline">
            {running === "webhook_retry" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            Webhook retry (idempotency)
          </Button>
          <Button onClick={() => runScenario("duplicate_retries")} disabled={!!running} variant="outline">
            {running === "duplicate_retries" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            3× duplicate retries
          </Button>
          <Button onClick={() => runScenario("mixed_partial_then_cancel")} disabled={!!running} variant="outline">
            {running === "mixed_partial_then_cancel" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            Partial + cancel
          </Button>
        </div>
      </div>

      {checklist.length > 0 && (
        <div className="bg-panel border border-border p-6 mb-6">
          <h3 className="font-display tracking-widest text-sm mb-4">CHECKLIST</h3>
          <ul className="space-y-3">
            {checklist.map((it) => (
              <li key={it.key} className="flex gap-3 items-start text-sm font-body">
                <StatusIcon s={it.status} />
                <div className="flex-1">
                  <p>{it.label}</p>
                  {it.detail && <p className="text-xs text-muted-foreground font-mono mt-0.5">{it.detail}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {lastResult && (
        <div className="bg-panel border border-border p-6 mb-6">
          <h3 className="font-display tracking-widest text-sm mb-4">LAST RESULT</h3>
          {lastResult.steps ? (
            <div className="space-y-2 text-sm font-body">
              <p className="text-xs font-mono text-muted-foreground">{lastResult.steps.length} sequential refunds fired</p>
              {lastResult.steps.map((s: any, i: number) => (
                <div key={i} className="border border-border p-2 bg-background">
                  <p className="font-mono text-xs">#{i + 1} amount={fmtMoney(s.refund_amount)} · event={s.event_id.slice(0, 28)}…</p>
                </div>
              ))}
              <p className="mt-2">Final booking amount_paid: <strong>{fmtMoney(lastResult.booking?.amount_paid)}</strong></p>
              <p>Final payment_status: <Badge>{lastResult.booking?.payment_status}</Badge></p>
            </div>
          ) : lastResult.first_run ? (
            <div className="text-sm font-body space-y-2">
              <p>First run: <code className="font-mono text-xs">{lastResult.first_run.event_id}</code> → {lastResult.first_run.result?.status}</p>
              <p>Replay: {lastResult.replay?.skipped_idempotent
                ? <Badge variant="secondary">SKIPPED — idempotent ✓</Badge>
                : <Badge variant="destructive">NOT SKIPPED — idempotency FAILED</Badge>}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4 text-sm font-body">
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-widest font-mono">Synthetic event</p>
                <p className="font-mono text-xs break-all">{lastResult.event_id}</p>
                <p className="mt-2">Refund amount: <strong>{fmtMoney(lastResult.refund_amount)}</strong></p>
                <p>Booking payment_status: <Badge>{lastResult.booking?.payment_status}</Badge></p>
                <p>Booking amount_paid: {fmtMoney(lastResult.booking?.amount_paid)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-widest font-mono">Counts</p>
                <p>addon_stock_audit rows: {lastResult.latest_stock_audit?.length ?? 0}</p>
              </div>
            </div>
          )}
          {lastResult.latest_reconciliation && (
            <div className="mt-4 grid md:grid-cols-2 gap-3 text-[11px] font-mono">
              <div>
                <div className="text-muted-foreground uppercase mb-1">Before</div>
                <pre className="bg-background border border-border p-2 overflow-x-auto">{JSON.stringify(lastResult.latest_reconciliation.before_snapshot, null, 2)}</pre>
              </div>
              <div>
                <div className="text-muted-foreground uppercase mb-1">After</div>
                <pre className="bg-background border border-border p-2 overflow-x-auto">{JSON.stringify(lastResult.latest_reconciliation.after_snapshot, null, 2)}</pre>
              </div>
            </div>
          )}
          {lastResult.latest_reconciliation?.deltas && (
            <pre className="mt-4 p-3 bg-background border border-border text-[11px] font-mono overflow-x-auto">
{JSON.stringify(lastResult.latest_reconciliation.deltas, null, 2)}
            </pre>
          )}
        </div>
      )}

      <div className="bg-panel border border-border p-6">
        <h3 className="font-display tracking-widest text-sm mb-4 flex items-center gap-2">
          <RotateCw className="w-4 h-4" /> WEBHOOK REPLAY
        </h3>
        <p className="font-body text-sm text-muted-foreground mb-3">
          Replays a Stripe event once. If a reconciliation row already exists for the
          event id, the call returns skipped_idempotent=true.
        </p>
        <div className="flex gap-2">
          <Input value={replayEventId} onChange={(e) => setReplayEventId(e.target.value)}
            placeholder="evt_… (synthetic or real)" />
          <Button onClick={runReplay} disabled={replaying}>
            {replaying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCw className="w-4 h-4 mr-1" />}
            Replay once
          </Button>
        </div>
        {replayResult && (
          <div className="mt-4 p-3 border border-border bg-background text-sm font-body">
            <p>
              {replayResult.skipped_idempotent ? (
                <Badge variant="secondary">SKIPPED — idempotent</Badge>
              ) : (
                <Badge>PROCESSED</Badge>
              )}
              <span className="ml-3 font-mono text-xs text-muted-foreground">
                {replayResult.event_type} · status={replayResult.status}
              </span>
            </p>
            {replayResult.existing_reconciliation && (
              <p className="text-xs text-muted-foreground font-mono mt-2">
                Prior reconciliation: {replayResult.existing_reconciliation.id.slice(0, 8)} at {new Date(replayResult.existing_reconciliation.created_at).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminRefundTest;